import type {DefaultError} from "@tanstack/react-query";
import {keepPreviousData, useQueryClient} from "@tanstack/react-query";
import {useParams} from "react-router";
import type {AxiosError} from "axios";
import {type UrlQueryKey, useCustomMutation, useCustomQuery} from "~/query/queryClient.ts";
import {useStores} from "~/stores";
import {actionStatusRequest, toActionInput} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import type {DatabagRouteMatch} from "~/routing/catRoutes.ts";
import type {
    PtV2Action,
    PtV2ActionInput,
    PtV2CatalogRow,
    PtV2CreateInitiativeResult,
    PtV2DiscountOffer,
    PtV2DistributionRow,
    PtV2Harmonization,
    PtV2ImportRecord,
    PtV2InitiativeInput,
    PtV2InitiativeOptions,
    PtV2LeakageRow,
    PtV2Playbook,
    PtV2Profile,
    PtV2Sources,
    PtV2Summary,
    PtV2Taxonomy,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";

export function paymentTermsV2Prefix(databagId: number | undefined): string {
    return `/payment_terms_v2/${databagId ?? 0}/`;
}

/** The databag in the route, falling back to the loaded bag. Never a fixed id. */
export function usePaymentTermsV2DatabagId(): number | undefined {
    const {id} = useParams<DatabagRouteMatch>();
    const {bagStore} = useStores();
    const fromRoute = Number(id);
    if (Number.isFinite(fromRoute) && fromRoute > 0) return fromRoute;
    return bagStore.bagId || undefined;
}

function usePtV2Get<T>(action: string, config?: UrlQueryKey[1], enabled = true, keepPrevious = false) {
    const databagId = usePaymentTermsV2DatabagId();
    const url = paymentTermsV2Prefix(databagId) + action;
    const queryKey: UrlQueryKey = config ? [url, config] : [url];
    return useCustomQuery<T, DefaultError, T, UrlQueryKey>({
        queryKey,
        enabled: enabled && Boolean(databagId),
        // Changing a parameter (the harmonization target) keeps the last rows on screen while refetching.
        placeholderData: keepPrevious ? keepPreviousData : undefined,
    });
}

export const usePtV2SummaryQuery = () => usePtV2Get<PtV2Summary>('summary/');
export const usePtV2LeakageQuery = (enabled = true) => usePtV2Get<PtV2LeakageRow[]>('leakage/', undefined, enabled);
export const usePtV2DiscountsQuery = (enabled = true) => usePtV2Get<PtV2DiscountOffer[]>('discounts/', undefined, enabled);
export const usePtV2CatalogQuery = (enabled = true) => usePtV2Get<PtV2CatalogRow[]>('catalog/', undefined, enabled);
export const usePtV2DistributionQuery = (enabled = true) => usePtV2Get<PtV2DistributionRow[]>('distribution/', undefined, enabled);
export const usePtV2ProfileQuery = (enabled = true) => usePtV2Get<PtV2Profile | null>('profile/', undefined, enabled);
export const usePtV2SourcesQuery = (enabled = true) => usePtV2Get<PtV2Sources>('sources/', undefined, enabled);
export const usePtV2ActionsQuery = (enabled = true) => usePtV2Get<PtV2Action[]>('actions/', undefined, enabled);
export const usePtV2ImportsQuery = (enabled = true) => usePtV2Get<PtV2ImportRecord[]>('imports/', undefined, enabled);
export const usePtV2TaxonomyQuery = (enabled = true) => usePtV2Get<PtV2Taxonomy | null>('taxonomy/', undefined, enabled);
export const usePtV2PlaybookQuery = (enabled = true) => usePtV2Get<PtV2Playbook | null>('playbook/', undefined, enabled);
export const usePtV2InitiativeOptionsQuery = (enabled = true) =>
    usePtV2Get<PtV2InitiativeOptions>('initiative-options/', undefined, enabled);

export function usePtV2HarmonizationQuery(targetDays: number, enabled = true) {
    return usePtV2Get<PtV2Harmonization>('harmonization/', {params: {target_days: targetDays}}, enabled, true);
}

/**
 * Check a plan item on or off. An existing action gets only its status (`PATCH actions/{id}/`), so a shared
 * `leakage:` / `harmonization:` action keeps the title and value it was stored with; a new key is upserted.
 */
export function usePtV2SetActionStatusMutation() {
    const databagId = usePaymentTermsV2DatabagId();
    const qc = useQueryClient();
    const url = paymentTermsV2Prefix(databagId) + 'actions/';
    const mutation = useCustomMutation<PtV2Action>({
        onSuccess: () => qc.invalidateQueries({queryKey: [url]}),
    });
    return {
        ...mutation,
        setStatus: (item: PtV2ActionInput, existing: PtV2Action | undefined, status: PtV2Action['status']) => {
            const req = actionStatusRequest(item, existing, status);
            return req.method === 'PATCH'
                ? mutation.mutateAsync({url: `${url}${req.id}/`, method: 'PATCH', data: req.data})
                : mutation.mutateAsync({url, method: 'POST', data: req.data});
        },
    };
}

export function usePtV2DeleteActionMutation() {
    const databagId = usePaymentTermsV2DatabagId();
    const qc = useQueryClient();
    const url = paymentTermsV2Prefix(databagId) + 'actions/';
    const mutation = useCustomMutation<unknown>({
        onSuccess: () => qc.invalidateQueries({queryKey: [url]}),
    });
    return {
        ...mutation,
        remove: (id: number) => mutation.mutateAsync({url: `${url}${id}/`, method: 'DELETE'}),
    };
}

/**
 * Make a SIT savings initiative from a plan item: upsert the action by key unless it already exists,
 * then `POST actions/{id}/create_initiative/`. Refetches the action list either way.
 */
export function usePtV2CreateInitiativeMutation() {
    const databagId = usePaymentTermsV2DatabagId();
    const qc = useQueryClient();
    const url = paymentTermsV2Prefix(databagId) + 'actions/';
    const upsertM = useCustomMutation<PtV2Action, AxiosError>();
    const createM = useCustomMutation<PtV2CreateInitiativeResult, AxiosError>({
        onSettled: () => qc.invalidateQueries({queryKey: [url]}),
    });
    return {
        isPending: upsertM.isPending || createM.isPending,
        reset: () => {
            upsertM.reset();
            createM.reset();
        },
        create: async (item: PtV2ActionInput, existing: PtV2Action | undefined, input: PtV2InitiativeInput) => {
            // Re-posting an existing key would overwrite its status, so reuse the stored action.
            const action = existing ?? await upsertM.mutateAsync({url, method: 'POST', data: toActionInput(item)});
            return createM.mutateAsync({url: `${url}${action.id}/create_initiative/`, method: 'POST', data: input});
        },
    };
}

/** Refetch everything for this databag after new data arrives. */
function useInvalidateDatabag(prefix: string) {
    const qc = useQueryClient();
    return () => qc.invalidateQueries({
        predicate: q => typeof q.queryKey[0] === 'string' && q.queryKey[0].startsWith(prefix),
    });
}

/** Staff only: pull the latest workpapers from BigQuery now. */
export function usePtV2SyncMutation() {
    const databagId = usePaymentTermsV2DatabagId();
    const prefix = paymentTermsV2Prefix(databagId);
    const invalidate = useInvalidateDatabag(prefix);
    const mutation = useCustomMutation<PtV2ImportRecord, AxiosError>({
        // A failed sync is recorded in the history too.
        onSettled: invalidate,
    });
    return {
        ...mutation,
        sync: () => mutation.mutateAsync({url: prefix + 'sync/', method: 'POST'}),
    };
}

/** Staff only: upload a zip of Hermes exports, then refetch everything for this databag. */
export function usePtV2ImportMutation() {
    const databagId = usePaymentTermsV2DatabagId();
    const prefix = paymentTermsV2Prefix(databagId);
    const invalidate = useInvalidateDatabag(prefix);
    const mutation = useCustomMutation<PtV2ImportRecord, AxiosError>({
        onSettled: invalidate,
    });
    return {
        ...mutation,
        upload: (file: File) => {
            const data = new FormData();
            data.append('file', file);
            // The client defaults to JSON, which would stringify FormData; multipart lets the browser add the boundary.
            return mutation.mutateAsync({url: prefix + 'import/', method: 'POST', data, headers: {'Content-Type': 'multipart/form-data'}});
        },
    };
}
